import { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/apiClient.js';
import { useAuth } from '../../lib/auth.js';

interface ProfileData {
  id: string;
  email: string | null;
  handle: string;
  firstName: string;
  lastName: string | null;
  displayName: string;
  dob: string | null;
  gender: string | null;
  country: string;
  status: string;
}

interface SocialChannel {
  type: string;
  handle: string;
}

const countries = ['US', 'CA', 'GB', 'IN', 'DE', 'FR', 'AU', 'BR', 'JP'];
const genders = ['male', 'female', 'other', 'prefer_not_to_say'];
const socialTypes = ['Instagram', 'TikTok', 'YouTube', 'X', 'LinkedIn', 'Twitch'];

export function ProfileModule(): JSX.Element {
  const { refresh } = useAuth();
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('US');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState('');
  const [creatorType, setCreatorType] = useState('INDIVIDUAL');
  const [channels, setChannels] = useState<SocialChannel[]>([]);
  const [creatorId, setCreatorId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creatorSaving, setCreatorSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    void apiGet<{ user: ProfileData }>('/api/v1/user/me').then((res) => {
      const value = res.data.user;
      setProfile(value);
      setFirstName(value.firstName ?? '');
      setLastName(value.lastName ?? '');
      setDisplayName(value.displayName ?? '');
      setCountry(value.country ?? 'US');
      setDob(value.dob ?? '');
      setGender(value.gender ?? '');
    }).catch((err) => setMessage((err as { message?: string })?.message ?? 'Could not load profile.'));
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPut('/api/v1/settings/profile', {
        firstName,
        ...(lastName ? { lastName } : { lastName: null }),
        displayName,
        country,
        ...(dob ? { dob } : {}),
        ...(gender ? { gender } : {}),
      });
      await refresh();
      setMessage('Profile saved.');
    } catch (err) {
      setMessage((err as { message?: string })?.message ?? 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  const addChannel = () => setChannels([...channels, { type: 'Instagram', handle: '' }]);
  const updateChannel = (index: number, key: keyof SocialChannel, value: string) => {
    setChannels(channels.map((channel, position) => position === index ? { ...channel, [key]: value } : channel));
  };
  const removeChannel = (index: number) => setChannels(channels.filter((_, position) => position !== index));

  const createCreatorProfile = async () => {
    if (!profile || !dob) {
      setMessage('Date of birth is required for a creator profile.');
      return;
    }
    setCreatorSaving(true);
    setMessage(null);
    try {
      await apiPut('/api/v1/settings/profile', { dob, ...(gender ? { gender } : {}) });
      const result = await apiPost<{ id: string }>('/api/v1/creators', { displayName, creatorType });
      setCreatorId(result.data.id);
      for (const channel of channels.filter((item) => item.handle.trim())) {
        await apiPost(`/api/v1/creators/${result.data.id}/social-accounts`, {
          platform: channel.type,
          platformAccountId: channel.handle.replace(/^@/, ''),
          handle: channel.handle.startsWith('@') ? channel.handle : `@${channel.handle}`,
        });
      }
      setMessage('Creator profile created.');
      await refresh();
    } catch (err) {
      setMessage((err as { message?: string })?.message ?? 'Could not create creator profile.');
    } finally {
      setCreatorSaving(false);
    }
  };

  return (
    <div className="profile-module">
      <div className="header"><h2>Profile</h2></div>
      {message && <p className="notice">{message}</p>}
      <section className="manager-card form-card">
        <h3>Personal information</h3>
        <label>First name<input value={firstName} onChange={(e) => setFirstName(e.target.value)} /></label>
        <label>Last name <span className="muted">(optional)</span><input value={lastName} onChange={(e) => setLastName(e.target.value)} /></label>
        <label>Display name<input value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></label>
        <label>Country<select value={country} onChange={(e) => setCountry(e.target.value)}>{countries.map((item) => <option key={item}>{item}</option>)}</select></label>
        <label>Email<input value={profile?.email ?? ''} disabled /></label>
        <label>Handle<input value={profile?.handle ?? ''} disabled /></label>
        <div className="actions"><button type="button" className="btn btn-primary" disabled={saving} onClick={() => void saveProfile()}>{saving ? 'Saving…' : 'Save profile'}</button></div>
      </section>

      <section className="manager-card form-card">
        <h3>Create a creator profile</h3>
        {creatorId ? <p className="notice success">Creator profile created: {creatorId}</p> : <>
          <p className="muted">Creator profiles require age verification and optional social channels.</p>
          <label>Date of birth<input type="date" value={dob} onChange={(e) => setDob(e.target.value)} /></label>
          <label>Gender <span className="muted">(optional)</span><select value={gender} onChange={(e) => setGender(e.target.value)}><option value="">Prefer not to say</option>{genders.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
          <label>Creator type<select value={creatorType} onChange={(e) => setCreatorType(e.target.value)}><option value="INDIVIDUAL">Individual</option><option value="CREATOR_BUSINESS">Creator business</option><option value="CREATOR_COLLECTIVE">Creator collective</option><option value="MEDIA_CREATOR">Media creator</option><option value="VIRTUAL_CREATOR">Virtual creator</option></select></label>
          <div className="social-channel-list"><h4>Social channels</h4>{channels.map((channel, index) => <div className="social-channel" key={`${channel.type}-${index}`}><select value={channel.type} onChange={(e) => updateChannel(index, 'type', e.target.value)}>{socialTypes.map((type) => <option key={type}>{type}</option>)}</select><input placeholder="@handle" value={channel.handle} onChange={(e) => updateChannel(index, 'handle', e.target.value)} /><button type="button" className="btn btn-danger" onClick={() => removeChannel(index)}>Remove</button></div>)}<button type="button" className="btn btn-outline" onClick={addChannel}>Add social channel</button></div>
          <div className="actions"><button type="button" className="btn btn-primary" disabled={creatorSaving} onClick={() => void createCreatorProfile()}>{creatorSaving ? 'Creating…' : 'Create creator profile'}</button></div>
        </>}
      </section>
    </div>
  );
}